/**
 * Confirmação de "Assinar agora" durante o período de teste (chamado 7176).
 *
 * Valores e datas vêm prontos do backend (`trial.activation_quotes`): o ciclo
 * pago começa hoje e a próxima cobrança é um ciclo depois. O texto foi
 * aprovado para destacar a continuidade do acesso — sem falar em "perder" os
 * dias restantes do teste.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ImportantNotice } from '@/components/subscription/ImportantNotice';
import { formatBrasilia } from '@/lib/brasiliaDate';
import { formatBRL } from '@/lib/currency';
import { yearlyDiscountPercent } from '@/lib/plan-pricing';
import { TRIAL_SUBSCRIBE_COPY } from '@/lib/trial-subscription';
import { cn } from '@/lib/utils';
import type { TrialActivationQuote } from '@/services/account.service';

type BillingCycle = TrialActivationQuote['billing_cycle'];

interface TrialSubscribeDialogProps {
  open: boolean;
  planName: string;
  quotes: TrialActivationQuote[];
  submitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (billingCycle: BillingCycle) => void;
}

const ROTULO_DO_CICLO: Record<BillingCycle, string> = { monthly: 'Mensal', yearly: 'Anual' };

const economiaDoAnual = (quotes: TrialActivationQuote[]): number =>
  yearlyDiscountPercent(
    quotes.find((q) => q.billing_cycle === 'monthly')?.amount ?? 0,
    quotes.find((q) => q.billing_cycle === 'yearly')?.amount ?? 0,
  );

export function TrialSubscribeDialog({
  open,
  planName,
  quotes,
  submitting,
  onOpenChange,
  onConfirm,
}: Readonly<TrialSubscribeDialogProps>) {
  const [ciclo, setCiclo] = useState<BillingCycle>('monthly');

  // Cada abertura recomeça no primeiro ciclo oferecido. Depende do ciclo (um
  // texto), não do array: cada nova busca da assinatura traz outro array e
  // desfaria a escolha feita com a confirmação aberta.
  const primeiroCiclo = quotes[0]?.billing_cycle ?? 'monthly';
  useEffect(() => {
    if (open) setCiclo(primeiroCiclo);
  }, [open, primeiroCiclo]);

  const cotacao = quotes.find((q) => q.billing_cycle === ciclo) ?? quotes[0];
  if (!cotacao) return null;

  const economia = economiaDoAnual(quotes);
  const valor = formatBRL(cotacao.amount);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{TRIAL_SUBSCRIBE_COPY.title(planName)}</DialogTitle>
          <DialogDescription>{TRIAL_SUBSCRIBE_COPY.description(planName)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {quotes.length > 1 && (
            <div className="flex justify-center">
              <fieldset className="inline-flex items-center p-1 bg-muted rounded-full border-0 m-0 min-w-0">
                <legend className="sr-only">Periodicidade</legend>
                {quotes.map((q) => (
                  <button
                    key={q.billing_cycle}
                    type="button"
                    aria-pressed={ciclo === q.billing_cycle}
                    onClick={() => setCiclo(q.billing_cycle)}
                    className={cn(
                      'px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2',
                      ciclo === q.billing_cycle
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {ROTULO_DO_CICLO[q.billing_cycle]}
                    {q.billing_cycle === 'yearly' && economia > 0 && (
                      <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">
                        -{economia}%
                      </span>
                    )}
                  </button>
                ))}
              </fieldset>
            </div>
          )}

          <ul className="bg-muted/30 rounded-lg p-4 border border-border/40 space-y-2 text-sm">
            <li className="flex items-baseline justify-between gap-4">
              <span className="text-muted-foreground">{TRIAL_SUBSCRIBE_COPY.paymentToday}</span>
              <span className="font-semibold">{valor}</span>
            </li>
            <li className="flex items-baseline justify-between gap-4">
              <span className="text-muted-foreground">{TRIAL_SUBSCRIBE_COPY.nextCharge}</span>
              <span className="font-semibold">{formatBrasilia(cotacao.next_charge_date, 'dd/MM/yyyy')}</span>
            </li>
          </ul>

          <ImportantNotice>{TRIAL_SUBSCRIBE_COPY.paymentNotice}</ImportantNotice>

          {ciclo === 'yearly' && (
            <p className="text-xs text-muted-foreground">{TRIAL_SUBSCRIBE_COPY.annualNote}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {TRIAL_SUBSCRIBE_COPY.dismiss}
          </Button>
          <Button onClick={() => onConfirm(cotacao.billing_cycle)} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {TRIAL_SUBSCRIBE_COPY.confirm(valor)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TrialSubscribeDialog;
